import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Shield, Send, Loader2, Bot, User, Trash2, Pencil, Plus, Check, X,
  Stethoscope, Briefcase, Users, Home, Car, Landmark, Gift, GraduationCap, CircleDot,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const DOMAINS = ["medical", "work", "family", "home", "auto", "finance", "social", "school", "other"] as const;
type Domain = (typeof DOMAINS)[number];

const DOMAIN_META: Record<Domain, { label: string; icon: typeof Shield }> = {
  medical: { label: "Medical & Dental", icon: Stethoscope },
  work: { label: "Work", icon: Briefcase },
  family: { label: "Family & Kids", icon: Users },
  home: { label: "Home", icon: Home },
  auto: { label: "Auto", icon: Car },
  finance: { label: "Finances & Insurance", icon: Landmark },
  social: { label: "Social & Celebrations", icon: Gift },
  school: { label: "Kids' School", icon: GraduationCap },
  other: { label: "Other", icon: CircleDot },
};

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
type Weekday = (typeof WEEKDAYS)[number];
const DAY_ABBR: Record<Weekday, string> = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun",
};

interface TimeBand { start: string; end: string; label?: string }

interface Guardrail {
  id: string;
  domain: Domain;
  name: string;
  preferredDays: Weekday[] | null;
  blackoutDays: Weekday[] | null;
  preferredTimeBands: TimeBand[] | null;
  neverTimeBands: TimeBand[] | null;
  timezone: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProposedGuardrail {
  domain: Domain;
  name: string;
  preferredDays?: Weekday[] | null;
  blackoutDays?: Weekday[] | null;
  preferredTimeBands?: TimeBand[] | null;
  neverTimeBands?: TimeBand[] | null;
  timezone?: string | null;
  notes?: string | null;
}

interface ChatMessage { role: "user" | "assistant"; content: string }

function bandText(bands: TimeBand[] | null | undefined): string {
  if (!bands?.length) return "";
  return bands.map((b) => `${b.start}–${b.end}`).join(", ");
}

export default function ZeusGuardrails() {
  const { toast } = useToast();

  const { data: guardrails = [], isLoading } = useQuery<Guardrail[]>({
    queryKey: ["/api/zeus/guardrails"],
  });

  // ── Chat / interview state ────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Editor dialog state ───────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formDomain, setFormDomain] = useState<Domain>("medical");
  const [formName, setFormName] = useState("");
  const [formPreferredDays, setFormPreferredDays] = useState<Weekday[]>([]);
  const [formBlackoutDays, setFormBlackoutDays] = useState<Weekday[]>([]);
  const [formPreferredBands, setFormPreferredBands] = useState<TimeBand[]>([]);
  const [formNeverBands, setFormNeverBands] = useState<TimeBand[]>([]);
  const [formTimezone, setFormTimezone] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formActive, setFormActive] = useState(true);

  const openCreate = () => {
    setEditingId(null);
    setFormDomain("medical");
    setFormName("");
    setFormPreferredDays([]);
    setFormBlackoutDays([]);
    setFormPreferredBands([]);
    setFormNeverBands([]);
    setFormTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || "");
    setFormNotes("");
    setFormActive(true);
    setDialogOpen(true);
  };

  const openEdit = (g: Guardrail) => {
    setEditingId(g.id);
    setFormDomain(g.domain);
    setFormName(g.name);
    setFormPreferredDays(g.preferredDays ?? []);
    setFormBlackoutDays(g.blackoutDays ?? []);
    setFormPreferredBands(g.preferredTimeBands ?? []);
    setFormNeverBands(g.neverTimeBands ?? []);
    setFormTimezone(g.timezone ?? "");
    setFormNotes(g.notes ?? "");
    setFormActive(g.isActive);
    setDialogOpen(true);
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (args: { id: string | null; payload: Record<string, unknown> }) => {
      const res = args.id
        ? await apiRequest("PATCH", `/api/zeus/guardrails/${args.id}`, args.payload)
        : await apiRequest("POST", "/api/zeus/guardrails", args.payload);
      return (await res.json()) as Guardrail;
    },
    onSuccess: (g, args) => {
      queryClient.invalidateQueries({ queryKey: ["/api/zeus/guardrails"] });
      setDialogOpen(false);
      toast({ title: args.id ? "Rule updated" : "Rule created", description: `"${g.name}" (${DOMAIN_META[g.domain]?.label ?? g.domain})` });
    },
    onError: (err: Error) =>
      toast({ title: "Failed to save rule", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/zeus/guardrails/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/zeus/guardrails"] });
      toast({ title: "Rule deleted" });
    },
    onError: (err: Error) =>
      toast({ title: "Failed to delete rule", description: err.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async (args: { id: string; isActive: boolean }) => {
      const res = await apiRequest("PATCH", `/api/zeus/guardrails/${args.id}`, { isActive: args.isActive });
      return (await res.json()) as Guardrail;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/zeus/guardrails"] }),
    onError: (err: Error) =>
      toast({ title: "Failed to update rule", description: err.message, variant: "destructive" }),
  });

  // Persist guardrails proposed by the interview (upsert by domain+name)
  const persistProposed = async (proposed: ProposedGuardrail[]) => {
    const current = queryClient.getQueryData<Guardrail[]>(["/api/zeus/guardrails"]) ?? guardrails;
    for (const p of proposed) {
      const payload = {
        domain: p.domain,
        name: p.name,
        preferredDays: p.preferredDays ?? null,
        blackoutDays: p.blackoutDays ?? null,
        preferredTimeBands: p.preferredTimeBands ?? null,
        neverTimeBands: p.neverTimeBands ?? null,
        timezone: p.timezone ?? null,
        notes: p.notes ?? null,
      };
      const existing = current.find(
        (g) => g.domain === p.domain && g.name.trim().toLowerCase() === p.name.trim().toLowerCase(),
      );
      try {
        if (existing) {
          await apiRequest("PATCH", `/api/zeus/guardrails/${existing.id}`, payload);
        } else {
          await apiRequest("POST", "/api/zeus/guardrails", payload);
        }
      } catch (err: any) {
        toast({ title: "Failed to save proposed rule", description: err?.message ?? String(err), variant: "destructive" });
      }
    }
    queryClient.invalidateQueries({ queryKey: ["/api/zeus/guardrails"] });
  };

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/zeus/guardrails/interview", {
        message,
        conversationHistory: messages,
      });
      return (await res.json()) as { response: string; proposedGuardrails: ProposedGuardrail[] };
    },
    onSuccess: async (data) => {
      setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
      if (data.proposedGuardrails?.length) {
        await persistProposed(data.proposedGuardrails);
      }
    },
    onError: (err: Error) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
      toast({ title: "Setup chat failed", description: err.message, variant: "destructive" });
    },
  });

  const sendChat = (text?: string) => {
    const value = (text ?? chatInput).trim();
    if (!value || chatMutation.isPending) return;
    setMessages((prev) => [...prev, { role: "user", content: value }]);
    setChatInput("");
    chatMutation.mutate(value);
  };

  const startInterview = () => {
    sendChat("I'm ready to set up my rules. Interview me about my life so you can build my scheduling guardrails.");
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = new Map<Domain, Guardrail[]>();
    for (const d of DOMAINS) map.set(d, []);
    for (const g of guardrails) {
      const key = (DOMAINS as readonly string[]).includes(g.domain) ? g.domain : "other";
      map.get(key as Domain)!.push(g);
    }
    return map;
  }, [guardrails]);

  const coreDomains = DOMAINS.filter((d) => d !== "other");
  const coveredCount = coreDomains.filter((d) => (grouped.get(d)?.length ?? 0) > 0).length;

  const toggleDay = (list: Weekday[], set: (v: Weekday[]) => void, day: Weekday) => {
    set(list.includes(day) ? list.filter((d) => d !== day) : [...list, day]);
  };

  const handleSave = () => {
    if (!formName.trim()) {
      toast({ title: "Name required", description: "Give this rule a short name.", variant: "destructive" });
      return;
    }
    const badBand = [...formPreferredBands, ...formNeverBands].find(
      (b) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(b.start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(b.end),
    );
    if (badBand) {
      toast({ title: "Invalid time band", description: "Times must be 24-hour HH:MM (e.g. 06:00).", variant: "destructive" });
      return;
    }
    saveMutation.mutate({
      id: editingId,
      payload: {
        domain: formDomain,
        name: formName.trim(),
        preferredDays: formPreferredDays.length ? formPreferredDays : null,
        blackoutDays: formBlackoutDays.length ? formBlackoutDays : null,
        preferredTimeBands: formPreferredBands.length ? formPreferredBands : null,
        neverTimeBands: formNeverBands.length ? formNeverBands : null,
        timezone: formTimezone.trim() || null,
        notes: formNotes.trim() || null,
        isActive: formActive,
      },
    });
  };

  const bandEditor = (bands: TimeBand[], set: (v: TimeBand[]) => void, testPrefix: string) => (
    <div className="space-y-2">
      {bands.map((b, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            className="w-28"
            placeholder="06:00"
            value={b.start}
            onChange={(e) => set(bands.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))}
            data-testid={`input-${testPrefix}-start-${i}`}
          />
          <span className="text-muted-foreground">to</span>
          <Input
            className="w-28"
            placeholder="10:30"
            value={b.end}
            onChange={(e) => set(bands.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))}
            data-testid={`input-${testPrefix}-end-${i}`}
          />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => set(bands.filter((_, j) => j !== i))}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="gap-1" onClick={() => set([...bands, { start: "", end: "" }])} data-testid={`button-add-${testPrefix}`}>
        <Plus className="h-3.5 w-3.5" /> Add time band
      </Button>
    </div>
  );

  const dayPicker = (selected: Weekday[], set: (v: Weekday[]) => void, testPrefix: string) => (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAYS.map((day) => (
        <button
          key={day}
          type="button"
          onClick={() => toggleDay(selected, set, day)}
          data-testid={`button-${testPrefix}-${day}`}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
            selected.includes(day)
              ? "border-yellow-500/70 bg-yellow-500/15 text-foreground"
              : "border-border/60 text-muted-foreground hover:bg-accent/50"
          }`}
        >
          {DAY_ABBR[day]}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 h-full">
      {/* ── LEFT: rules grouped by domain ──────────────────────────────────── */}
      <div className="flex w-1/2 min-h-0 flex-col border-r border-border/60">
        <div className="flex items-center gap-3 border-b border-border/40 px-4 py-3">
          <Shield className="h-4 w-4 text-yellow-500" />
          <span className="text-sm font-semibold">Your Rules</span>
          <span className="text-xs text-muted-foreground" data-testid="text-guardrail-progress">
            {coveredCount}/{coreDomains.length} life areas covered
          </span>
          <div className="ml-auto">
            <Button size="sm" className="gap-1.5" onClick={openCreate} data-testid="button-add-guardrail">
              <Plus className="h-4 w-4" /> Add rule
            </Button>
          </div>
        </div>
        {/* Setup progress bar */}
        <div className="border-b border-border/40 px-4 py-2">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-gradient-to-r from-yellow-400 to-amber-600 transition-all"
              style={{ width: `${(coveredCount / coreDomains.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : guardrails.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <Shield className="h-8 w-8 opacity-40" />
              <p className="text-sm font-medium">No rules yet</p>
              <p className="max-w-xs text-xs">
                Rules are the guardrails for how Zeus can operate — when it may schedule appointments, which days are off-limits, and how your life works. Start the setup interview on the right, or add a rule manually.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {DOMAINS.map((domain) => {
                const items = grouped.get(domain) ?? [];
                if (items.length === 0) return null;
                const Meta = DOMAIN_META[domain];
                const Icon = Meta.icon;
                return (
                  <div key={domain}>
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" /> {Meta.label}
                    </div>
                    <div className="space-y-2">
                      {items.map((g) => (
                        <div
                          key={g.id}
                          className={`w-full rounded-lg border p-3 ${g.isActive ? "border-border/60" : "border-border/40 opacity-60"}`}
                          data-testid={`row-guardrail-${g.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-semibold">{g.name}</span>
                                {!g.isActive && <Badge variant="secondary" className="text-[10px]">paused</Badge>}
                              </div>
                              <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                                {g.preferredDays?.length ? (
                                  <Badge variant="outline" className="gap-1 font-normal">
                                    <Check className="h-3 w-3 text-green-500" /> Best: {g.preferredDays.map((d) => DAY_ABBR[d] ?? d).join(" → ")}
                                  </Badge>
                                ) : null}
                                {g.blackoutDays?.length ? (
                                  <Badge variant="outline" className="gap-1 font-normal">
                                    <X className="h-3 w-3 text-red-500" /> Never: {g.blackoutDays.map((d) => DAY_ABBR[d] ?? d).join(", ")}
                                  </Badge>
                                ) : null}
                                {g.preferredTimeBands?.length ? (
                                  <Badge variant="outline" className="gap-1 font-normal">
                                    <Check className="h-3 w-3 text-green-500" /> {bandText(g.preferredTimeBands)}
                                  </Badge>
                                ) : null}
                                {g.neverTimeBands?.length ? (
                                  <Badge variant="outline" className="gap-1 font-normal">
                                    <X className="h-3 w-3 text-red-500" /> Never {bandText(g.neverTimeBands)}
                                  </Badge>
                                ) : null}
                                {g.timezone ? (
                                  <Badge variant="outline" className="font-normal">{g.timezone}</Badge>
                                ) : null}
                              </div>
                              {g.notes ? (
                                <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{g.notes}</p>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              <Switch
                                checked={g.isActive}
                                onCheckedChange={(v) => toggleMutation.mutate({ id: g.id, isActive: v })}
                                data-testid={`switch-guardrail-active-${g.id}`}
                              />
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(g)} data-testid={`button-edit-guardrail-${g.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => deleteMutation.mutate(g.id)}
                                disabled={deleteMutation.isPending}
                                data-testid={`button-delete-guardrail-${g.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: setup interview chat ───────────────────────────────────── */}
      <div className="flex w-1/2 min-h-0 flex-col">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Setup interview</span>
          <span className="ml-auto text-xs text-muted-foreground">Zeus, your life-efficiency coach</span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
              <Bot className="h-8 w-8 opacity-40" />
              <p className="text-sm font-medium text-foreground">Let Zeus learn how your life works</p>
              <p className="max-w-sm text-xs">
                Zeus will interview you like a life coach — your work schedule, family commitments, and when appointments do and don't work — and build your rules live as you answer. You can skip any topic and come back anytime.
              </p>
              <Button onClick={startInterview} className="mt-1 gap-1.5" data-testid="button-start-interview">
                <Bot className="h-4 w-4" /> Start setup interview
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-600">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${
                      m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                    }`}
                  >
                    {m.content}
                  </div>
                  {m.role === "user" && (
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
              {chatMutation.isPending && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Zeus is thinking…</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        <div className="border-t border-border/40 p-3">
          <div className="flex gap-2">
            <Textarea
              rows={1}
              placeholder={messages.length === 0 ? "Or just start typing about your schedule…" : "Answer Zeus…"}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              className="min-h-0 resize-none"
              data-testid="input-interview-message"
            />
            <Button onClick={() => sendChat()} disabled={chatMutation.isPending || !chatInput.trim()} data-testid="button-send-interview">
              {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Full-page create/edit dialog ──────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit rule" : "New rule"}</DialogTitle>
          </DialogHeader>
          <div className="mx-auto w-full max-w-2xl space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Life area</Label>
                <Select value={formDomain} onValueChange={(v) => setFormDomain(v as Domain)}>
                  <SelectTrigger data-testid="select-guardrail-domain"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOMAINS.map((d) => (
                      <SelectItem key={d} value={d}>{DOMAIN_META[d].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="guardrail-name">Name</Label>
                <Input
                  id="guardrail-name"
                  placeholder="e.g. Doctor & dentist appointments"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  data-testid="input-guardrail-name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Best days (click in order of preference)</Label>
              {dayPicker(formPreferredDays, setFormPreferredDays, "preferred-day")}
              {formPreferredDays.length > 0 && (
                <p className="text-xs text-muted-foreground">Order: {formPreferredDays.map((d) => DAY_ABBR[d]).join(" → ")}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Never schedule on</Label>
              {dayPicker(formBlackoutDays, setFormBlackoutDays, "blackout-day")}
            </div>

            <div className="space-y-1.5">
              <Label>Preferred time bands (24h)</Label>
              {bandEditor(formPreferredBands, setFormPreferredBands, "preferred-band")}
            </div>

            <div className="space-y-1.5">
              <Label>Never schedule between (24h)</Label>
              {bandEditor(formNeverBands, setFormNeverBands, "never-band")}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="guardrail-tz">Timezone</Label>
                <Input
                  id="guardrail-tz"
                  placeholder="e.g. America/New_York"
                  value={formTimezone}
                  onChange={(e) => setFormTimezone(e.target.value)}
                  data-testid="input-guardrail-timezone"
                />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={formActive} onCheckedChange={setFormActive} data-testid="switch-guardrail-form-active" />
                <span className="text-sm">{formActive ? "Active" : "Paused"}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="guardrail-notes">Notes</Label>
              <Textarea
                id="guardrail-notes"
                rows={3}
                placeholder="Anything else Zeus should know for this life area…"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                data-testid="input-guardrail-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending} className="gap-1.5" data-testid="button-save-guardrail">
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
